import { type ComputedRef, computed, useStore } from "@nuxtjs/composition-api";

import { getExperimentsStore } from "@/store/experiments";
import { type AbTestPair } from "@/types";
/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useFlag
 * @desc     A composable that fetches and returns the state of a feature flag.
 *           It uses the experiments store to fetch the flag and compute its state.
 *
 * @param    {string} name - The name of the feature flag to check.
 * @returns  {ComputedRef<boolean>} A computed property that returns true if the flag is enabled, false otherwise.
 *
 * @summary  Composable used to check the state of a feature flag
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export const useFlag = (name: string) => {
  const store = useStore();
  const experimentsStore = getExperimentsStore(store);
  experimentsStore.fetchFeatureFlag({ flagName: name });
  return computed(() => experimentsStore.hasFlag(name));
};
/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useVariant
 * @desc     A composable that fetches and returns the variant of a feature flag.
 *           It uses the experiments store to fetch the flag and compute its variant.
 *
 * @param    {string} name - The name of the feature flag to check.
 * @returns  {ComputedRef<AbTestPair>} A computed property that returns the variant of the feature flag.
 *
 * @summary  Composable used to get the variant of a feature flag
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export const useVariant = (name: string): ComputedRef<AbTestPair> => {
  const store = useStore();
  const experimentsStore = getExperimentsStore(store);
  experimentsStore.fetchFeatureFlag({ flagName: name });
  return computed(() => experimentsStore.variant(name));
};
